import type { MeshNode, SimulatedNode } from '../api/types';
import { type ProtocolFilter } from '../state/AppData';
import { type ResolvedProtocol, resolveNodeProtocol } from './nodeProtocol';
import { roleCategoryFor, OUT_OF_SCOPE_DIM, PROTOCOL_COLOR, UNKNOWN_PROTOCOL_COLOR, ROLE_CATEGORY_ORDER, ROLE_SHADE } from './definitions/protocols';
import { regionInScope } from './regions';
import { dimmedByTags } from './nodeTags';
import { type ExpressionSpecification, LngLatBounds } from 'maplibre-gl';
import { type ThemeMode, colorFor } from './definitions/palette';
import { hasUsablePosition } from './meshview';
import { MESH_FIT_OUTLIER_MAX_DEGREES_FROM_MEDIAN } from './definitions/map';
import { NON_REPEATER_DIM, dotScale, drawOrder, scopeMarker, type MapScopesPayload, type ScopeMarker } from './mapScopes';


// Basemap style/tiles/theme handling moved to lib/mapStyle.ts (2026-09-23)
// so NodeDetailPage's position-history map can reuse the exact same tile
// source and theme config instead of reimplementing it — see that file for
// the full "why OSM raster tiles, why the dark variant is a paint tweak"
// reasoning.

// --- Node feature building --------------------------------------------
// Rebuilt for readability: the map was unreadable at
// real density (shape-by-protocol, a freshness ring, and per-role colour
// all stacked on the same small dots). Colour is now the ONLY channel —
// see the "Map marker colour scheme" block in lib/definitions.ts for the
// two-mode scheme (protocol-only in ALL mode, role-category in single-
// protocol mode) and why. Every node is now a plain, same-shape, same-size
// dot; nothing here computes a shape, a stroke, or a freshness-driven ring
// opacity anymore. Provenance (verified/unverified) also moved OFF the map
// entirely as part of the same declutter — it's fully covered by the Node
// List and Node Detail pages (ObserverBadge everywhere), which is where
// "which observer, how trusted" actually needs to be read in detail rather
// than skimmed at map scale.
//
// Protocol still comes ONLY from node.protocol (lib/nodeProtocol.ts) — no
// role-name or id-format guessing (see that file for the full account of
// why this matters). A node with no protocol reported yet still renders,
// coloured as its own distinct "unknown protocol" grey rather than guessed
// into either protocol's colour — but only in ALL mode. Single-protocol
// mode excludes it from the map entirely, same as the other protocol's own
// nodes (protocol is a separation, not an emphasis — see
// nodesToGeoJSON's own comment on the exclusion below).
export interface RegionScopeArgs {
  selectedCount: number;
  hideOutside: boolean;
  regionsForNode: (n: { lat: number | null; lon: number | null }) => string[];
}


export function nodesToGeoJSON(
  nodes: Map<string, MeshNode>,
  filter: ProtocolFilter,
  region: RegionScopeArgs,
  selectedNodeId: string | null,
  nearRegionBoundary: (n: { lat: number | null; lon: number | null }) => boolean,
  tagFilter: readonly string[],
  // Scope mode (issue #24): the stored scope map; null = Role mode. In Scope mode each repeater feature carries its
  // scope colour and a tooltip naming its scopes; everything else is unchanged.
  scopeView: MapScopesPayload | null = null,
) {
  const features = [];
  // Nodes with no known position (lat/lon null or missing) are NEVER drawn
  // on the map — no default centre, no (0,0), no jitter, no borrowed last-
  // known value. This was reported as a real bug (a node with a null
  // position appearing somewhere on the map); audited and reproduced live
  // against the backend (2026-09-20): this exact guard already excludes it
  // (verified via map.querySourceFeatures — zero rendered features for that
  // node id). Kept here, explicit and commented, specifically so a future
  // edit doesn't reintroduce a fallback. They're surfaced instead as their
  // own list (see UnpositionedNodesNote) — real nodes with real packets,
  // just nowhere to draw them.
  const unpositionedNodes: { id: string; name: string }[] = [];
  let unknownProtocol = 0;
  for (const n of nodes.values()) {
    const protocol: ResolvedProtocol = resolveNodeProtocol(n);
    // Protocol is SEPARATION, not emphasis: swapping between meshtastic and
    // meshcore must not just dim the other protocol and leave it as visual
    // noise — the two protocols should be separated unless viewing the ALL
    // page. A node
    // whose protocol doesn't match an active single-protocol filter is
    // excluded from the map entirely — same treatment as "no known
    // position" below, never a dimmed layer underneath the protocol the
    // user actually asked for. An "unknown"-protocol node is excluded the
    // same way any non-matching protocol is (it's specifically not
    // confirmed to be the one protocol being viewed) — this falls out of
    // the plain `protocol === filter` comparison below without a special
    // case, and only ever matters in ALL mode, where unknown nodes still
    // render as their own honest grey marker as before.
    if (filter !== "all" && protocol !== filter) continue;
    if (protocol === "unknown") unknownProtocol += 1;
    if (n.lat == null || n.lon == null) {
      unpositionedNodes.push({ id: String(n.id), name: n.short_name || n.long_name || String(n.id) });
      continue;
    }
    // roleCategory is intrinsic to the node (which of the 4 shared buckets
    // its own protocol's role maps to) — computed once regardless of which
    // colour mode is currently active; only the PAINT expression (below)
    // decides whether it's actually used to colour this dot. "unknown"
    // protocol has no role vocabulary to categorise, so it's left out —
    // those nodes are always coloured via the protocol fallback instead.
    const roleCategory = protocol === "unknown" ? null : roleCategoryFor(protocol, n.role);
    // Region membership (spec §10): only constrains scope once at least one
    // region is selected; a node whose membership can't be determined
    // (unknown geometry — see lib/regions.ts) is treated as in-scope rather
    // than penalized for a gap in the backend's data, consistent with
    // "dim, don't hide" defaulting to showing rather than hiding on doubt.
    // Shared with PacketFeed/TimelinePanel via lib/regions.ts's
    // regionInScope so map, feed, and histogram all agree on membership.
    // Region is emphasis, not separation — unlike protocol above, this
    // stays a dim (or, with "Hide others" checked, a hide) rather than an
    // unconditional exclusion — the region filter's dim/hide behaviour is
    // deliberately left as-is here.
    const nodeInRegionScope = regionInScope({ lat: n.lat, lon: n.lon }, { selectedCount: region.selectedCount, regionsForNode: region.regionsForNode });
    if (region.selectedCount > 0 && region.hideOutside && !nodeInRegionScope) continue;
    // Node selection (spec §5): "mirrors the region-dim behaviour ... at
    // node scope instead of region scope" — selecting a node dims every
    // other node the same way an out-of-region node dims, rather than
    // hiding them (a selection you can't back out of visually is worse
    // than no selection feedback at all). Deliberately NOT extended to
    // "and its Heard By set" from the same spec line: heard_by is
    // observer-keyed (GET /api/nodes/{id} — confirmed live, 2026-09-20),
    // and observers have no position/marker on this map at all, so there
    // is nothing real to highlight for them here. Flagged as a backend/
    // data-model gap rather than approximated — see the engagement notes.
    const nodeSelectionInScope = selectedNodeId == null || String(n.id) === selectedNodeId;
    // Tag filter: emphasis, not separation -- a node without any of the chosen tags is dimmed like
    // an out-of-region node, never hidden.
    const inScope = nodeInRegionScope && nodeSelectionInScope && !dimmedByTags(n.tags, tagFilter);
    // Boundary-node treatment (spec §10) — a soft heuristic cue, only
    // evaluated while a region filter is active (nearRegionBoundary always
    // returns false otherwise); see lib/regions.ts's nearAnyRegionBoundary.
    const nearBoundary = nearRegionBoundary({ lat: n.lat, lon: n.lon });
    const scope = scopeProperties(scopeView, protocol, roleCategory, String(n.id), n.short_name || n.long_name || String(n.id));
    const scopeKind = ("scopeKind" in scope ? scope.scopeKind : null) ?? null;
    features.push({
      type: "Feature" as const,
      geometry: { type: "Point" as const, coordinates: [n.lon, n.lat] },
      properties: {
        id: String(n.id),
        protocol,
        roleCategory: roleCategory ?? "other",
        dotScale: dotScale(protocol, roleCategory, scopeKind),
        drawOrder: drawOrder(protocol, roleCategory, scopeKind),
        inScope,
        nearBoundary,
        name: n.short_name || n.long_name || String(n.id),
        ...scope,
      },
    });
  }
  return { collection: { type: "FeatureCollection" as const, features }, unpositionedNodes, unknownProtocol };
}


export const SCOPE_OPACITY_EXPR: ExpressionSpecification = ["case", ["get", "inScope"], 1, OUT_OF_SCOPE_DIM];


// Scope mode (issue #24). A repeater is a MeshCore node in the router_repeater role category (the same test the
// Role colours use); only repeaters get a scope colour, every other marker keeps its normal colour and is dimmed.
export function isRepeaterMarker(protocol: ResolvedProtocol, roleCategory: string | null): boolean {
  return protocol === "meshcore" && roleCategory === "router_repeater";
}


function scopeProperties(view: MapScopesPayload | null, protocol: ResolvedProtocol, roleCategory: string | null, id: string, name: string) {
  if (!view || !isRepeaterMarker(protocol, roleCategory)) return {};
  const m = scopeMarker(id, name, view);
  return { scopeColour: m.colour, scopeKind: m.kind, tip: m.tip };
}


/** The scope marker of every repeater drawn on the map (positioned, in the protocol filter): the legend counts these. */
export function repeaterScopeMarkers(nodes: Map<string, MeshNode>, filter: ProtocolFilter, view: MapScopesPayload): ScopeMarker[] {
  const out: ScopeMarker[] = [];
  for (const n of nodes.values()) {
    const protocol = resolveNodeProtocol(n);
    if (filter !== "all" && protocol !== filter) continue;
    if (n.lat == null || n.lon == null || protocol === "unknown") continue;
    if (!isRepeaterMarker(protocol, roleCategoryFor(protocol, n.role))) continue;
    out.push(scopeMarker(String(n.id), n.short_name || n.long_name || String(n.id), view));
  }
  return out;
}


// In Scope mode a marker without a scope colour (not a repeater) is dimmed; the region/selection dim still wins.
export const SCOPE_MODE_OPACITY_EXPR: ExpressionSpecification = [
  "case", ["get", "inScope"], ["case", ["has", "scopeColour"], 1, NON_REPEATER_DIM], OUT_OF_SCOPE_DIM,
];


// --- Simulated nodes (phase 5) ---------------------------------------------
// A genuinely separate source/layer from the real node roster above, kept
// deliberately independent so the already-verified real-node pipeline
// (colour scheme, region/protocol scoping, click-to-select) is untouched.
// Simulated nodes are never real observations, never click-to-select (there
// is no real node-detail record behind a synthetic id — the Simulated
// Nodes panel on the Messages page is their real home), and are excluded
// from the map entirely when they carry no position, same "never draw an
// unpositioned node" rule as the real roster.
export function simulatedNodesToGeoJSON(simNodes: SimulatedNode[]) {
  const features = [];
  for (const n of simNodes) {
    if (!n.position) continue;
    features.push({
      type: "Feature" as const,
      geometry: { type: "Point" as const, coordinates: [n.position.lon, n.position.lat] },
      properties: { id: n.id, protocol: n.protocol, name: n.label },
    });
  }
  return { type: "FeatureCollection" as const, features };
}


// Colour is the only channel now (see
// the header comment above and lib/definitions.ts's "Map marker colour
// scheme"). ALL mode: colour = protocol, full stop. Single-protocol mode:
// colour = the node's role category, one of four shared buckets. Which
// expression is active is swapped via setPaintProperty when the protocol
// toggle changes (see the effect below) rather than baked in at layer
// creation, same pattern already used for theme changes.
export function protocolColorExpr(theme: ThemeMode): ExpressionSpecification {
  return [
    "match",
    ["get", "protocol"],
    "meshtastic",
    colorFor(PROTOCOL_COLOR.meshtastic, theme),
    "meshcore",
    colorFor(PROTOCOL_COLOR.meshcore, theme),
    colorFor(UNKNOWN_PROTOCOL_COLOR, theme),
  ] as unknown as ExpressionSpecification;
}


// Role colour mode: the protocol's colour family, in the shade of the node's role category (ROLE_SHADE). A node of
// unknown protocol has no role vocabulary and keeps the neutral grey.
export function roleColorExpr(theme: ThemeMode): ExpressionSpecification {
  const byCategory = (protocol: "meshtastic" | "meshcore") => {
    const arms: unknown[] = ["match", ["get", "roleCategory"]];
    for (const cat of ROLE_CATEGORY_ORDER.slice(0, -1)) arms.push(cat, colorFor(ROLE_SHADE[protocol][cat], theme));
    arms.push(colorFor(ROLE_SHADE[protocol].other, theme));
    return arms;
  };
  return [
    "match", ["get", "protocol"],
    "meshtastic", byCategory("meshtastic"),
    "meshcore", byCategory("meshcore"),
    colorFor(UNKNOWN_PROTOCOL_COLOR, theme),
  ] as unknown as ExpressionSpecification;
}


export function nodeColorExpr(theme: ThemeMode, byScope = false): ExpressionSpecification {
  const base = roleColorExpr(theme);
  return byScope ? (["coalesce", ["get", "scopeColour"], base] as unknown as ExpressionSpecification) : base;
}


function median(values: number[]): number {
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 0 ? (sorted[mid - 1] + sorted[mid]) / 2 : sorted[mid];
}


// Bounding box of every node with a usable position (lib/meshview.ts's
// hasUsablePosition — the SAME rule that already decides what gets a dot on
// this map at all, reused rather than a second "is this real" definition),
// MINUS any position implausibly far from where the mesh actually is (see
// MESH_FIT_OUTLIER_MAX_DEGREES_FROM_MEDIAN's own comment, definitions.ts, for
// why: a single bad stored coordinate — found live, "XY Repeater 2" at
// 53.06E — otherwise defines the entire fit). This is NOT a regional
// filter: there is no hardcoded lat/lon box, no named region anywhere in
// this function — the median is recomputed from whatever
// positions exist every time this runs, so real MeshCore nodes ~800km from
// this deployment's coverage area are well
// within the generous margin and always land inside the fit like any other
// real node. Exclusion here is scoped to the CAMERA FIT ONLY — an excluded
// node is still a real node with a usable position; nodesToGeoJSON (the
// function that actually decides what gets drawn) never calls this and
// never sees this exclusion, so it still renders on the map exactly like
// any other node. Returns null when nothing has a usable position yet
// (nodes haven't loaded, or none do) — the caller's job to wait for a later
// poll rather than fit to an empty/degenerate box.
export function meshBoundsFromNodes(nodes: Map<string, MeshNode>): LngLatBounds | null {
  const usable: { lat: number; lon: number }[] = [];
  for (const n of nodes.values()) {
    if (hasUsablePosition(n)) usable.push({ lat: n.lat, lon: n.lon });
  }
  if (usable.length === 0) return null;

  const medianLat = median(usable.map((p) => p.lat));
  const medianLon = median(usable.map((p) => p.lon));
  const inRange = usable.filter(
    (p) => Math.hypot(p.lat - medianLat, p.lon - medianLon) <= MESH_FIT_OUTLIER_MAX_DEGREES_FROM_MEDIAN,
  );
  // Guards against a degenerate all-excluded case, which shouldn't be
  // reachable (the median position itself is always within range of
  // itself) but never fit to nothing if it somehow happened.
  const points = inRange.length > 0 ? inRange : usable;

  let bounds: LngLatBounds | null = null;
  for (const p of points) {
    if (!bounds) bounds = new LngLatBounds([p.lon, p.lat], [p.lon, p.lat]);
    else bounds.extend([p.lon, p.lat]);
  }
  return bounds;
}
